import { type KeywordList, createKeywordListEntity } from "app/modules/demand-analysis/keyword-lists";

import type { SearchTrendsByCountryData } from "./searchTrendsByCountry";

import type { ChartSeriesItem, TableData } from "./types";
import { countryById, getPeriodOverPeriodCountryColor } from "./utils";

type SearchTrendsByCountryDataItem = SearchTrendsByCountryData[number];
type GetKeywordListById = (keywordListId: string) => KeywordList | undefined;

const createComparisonDataItem = (
    getKeywordListById: GetKeywordListById,
    { countryId, data }: SearchTrendsByCountryDataItem,
): ChartSeriesItem | undefined => {
    if (!data.children) {
        return undefined;
    }

    const country = countryById(countryId);
    const transformedData = data.children
        .map((child) => {
            const entity = createKeywordListEntity(child);

            if (!entity) {
                return undefined;
            }

            return {
                keywordList: getKeywordListById(entity.id),
                volume: child.currentPeriod.volume,
                share: child.currentPeriod.volumeShare,
                change: child.currentPeriod.periodVolumeChange?.change ?? 0,
            };
        })
        .filter((group) => group !== undefined)
        .toSorted((left, right) => right.share - left.share);

    return {
        "hc-key": country.code,
        title: country.text,
        isComparison: true,
        value: transformedData[0]?.share,
        color: transformedData[0]?.keywordList?.color,
        tooltipData: transformedData,
    };
};

const createDataItem = (
    getColor: (value: number) => string | undefined,
    { countryId, data }: SearchTrendsByCountryDataItem,
): ChartSeriesItem | undefined => {
    const country = countryById(countryId);
    const isPreviousPeriod = "previousPeriod" in data;

    return {
        "hc-key": country.code,
        title: country.text,
        isComparison: false,
        value: data.currentPeriod.volumeShare,
        color: isPreviousPeriod ? getColor(data.currentPeriod.periodVolumeChange?.change ?? 0) : undefined,
        tooltipData: [
            {
                share: data.currentPeriod.volumeShare,
                volume: data.currentPeriod.volume,
                change: data.currentPeriod.periodVolumeChange?.change ?? 0,
                previousVolume: data.previousPeriod?.volume,
                previousShare: data.previousPeriod?.volumeShare,
            },
        ],
    };
};

const createTrafficDistribution = (
    getKeywordListById: GetKeywordListById,
    data: SearchTrendsByCountryDataItem["data"],
    isPreviousPeriod = false,
) => {
    if ("children" in data && data.children) {
        return data.children
            .map((child) => {
                const entity = createKeywordListEntity(child);
                const color = entity ? getKeywordListById(entity.id)?.color : undefined;

                if (isPreviousPeriod) {
                    return child.previousPeriod
                        ? {
                              name: child.name,
                              value: child.previousPeriod.volumeShare,
                              color,
                          }
                        : undefined;
                }

                return {
                    name: child.name,
                    value: child.currentPeriod.volumeShare,
                    color,
                };
            })
            .filter((item) => item !== undefined);
    }

    return undefined;
};

const createTableRow = (
    getKeywordListById: GetKeywordListById,
    { countryId, data }: SearchTrendsByCountryDataItem,
): TableData => {
    return {
        country: countryId,
        volumeShare: data.currentPeriod.volumeShare,
        change: data.currentPeriod.periodVolumeChange?.change ?? 0,
        currentVolume: data.currentPeriod.volume,
        previousVolume: data.previousPeriod?.volume,
        trafficDistribution: createTrafficDistribution(getKeywordListById, data),
        previousPeriodTrafficDistribution: createTrafficDistribution(getKeywordListById, data, true),
    };
};

export const selectSearchTrendsByCountryData = (
    getKeywordListById: GetKeywordListById,
    data: SearchTrendsByCountryData,
): {
    chartData: ChartSeriesItem[];
    tableData: TableData[];
} => {
    const values = data
        .map((item) => ("currentPeriod" in item.data ? item.data.currentPeriod.periodVolumeChange?.change : undefined))
        .filter((value) => value !== undefined);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const getColorByChange = (change: number) => getPeriodOverPeriodCountryColor(change, min, max);

    const chartData = data
        .map((item) =>
            "children" in item.data
                ? createComparisonDataItem(getKeywordListById, item)
                : createDataItem(getColorByChange, item),
        )
        .filter((dataItem) => dataItem !== undefined);

    const tableData = data.map((item) => createTableRow(getKeywordListById, item));

    return {
        chartData,
        tableData,
    };
};
